import React, { useState, useEffect, useCallback } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

const userLabel = (user) => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return name ? `${name} (${user.email})` : user.email;
};

const ProjectMembersPanel = ({ org_id, project_id, open }) => {
  const membersApi = `/api_super_admin/orgs/${org_id}/projects/${project_id}/project_users`;
  const usersApi = `/api_super_admin/orgs/${org_id}/users`;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${membersApi}?per_page=200`);
      const data = await response.json();
      setMembers(data.records || []);
    } catch (error) {
      setMessage({ type: "error", text: "Failed to load project members." });
    } finally {
      setLoading(false);
    }
  }, [membersApi]);

  const fetchAvailableUsers = useCallback(async (search) => {
    setSearchingUsers(true);
    try {
      const params = new URLSearchParams({ per_page: "30" });
      if (search) params.set("search", search);
      const response = await fetch(`${usersApi}?${params}`);
      const data = await response.json();
      const memberIds = new Set(members.map((m) => m.id));
      setUserOptions(
        (data.records || []).filter(
          (u) => u.role !== "super_admin" && !memberIds.has(u.id)
        )
      );
    } catch (error) {
      setUserOptions([]);
    } finally {
      setSearchingUsers(false);
    }
  }, [usersApi, members]);

  useEffect(() => {
    if (open) {
      fetchMembers();
    }
  }, [open, fetchMembers]);

  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => fetchAvailableUsers(searchInput), 250);
    return () => clearTimeout(timer);
  }, [open, searchInput, members, fetchAvailableUsers]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setSearchInput("");
      setMessage(null);
    }
  }, [open]);

  const clearMessage = () => {
    setTimeout(() => setMessage(null), 4000);
  };

  const handleAdd = async () => {
    if (!selectedUser) return;

    const addedLabel = userLabel(selectedUser);
    setAdding(true);
    setMessage(null);
    try {
      const response = await fetch(membersApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: selectedUser.email, role: "member" }),
      });
      const data = await response.json();

      if (data.errors) {
        setMessage({ type: "error", text: data.errors.join(", ") });
        clearMessage();
        return;
      }

      setMembers((prev) =>
        [...prev, data].sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""))
      );
      setSelectedUser(null);
      setSearchInput("");
      setMessage({ type: "success", text: `${addedLabel} added to project.` });
      clearMessage();
    } catch (error) {
      setMessage({ type: "error", text: "Failed to add member." });
      clearMessage();
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (member) => {
    const name = userLabel(member);
    if (!window.confirm(`Remove ${name} from this project?`)) return;

    setRemovingId(member.id);
    setMessage(null);
    try {
      const response = await fetch(`${membersApi}/${member.id}`, { method: "DELETE" });
      const data = await response.json();

      if (!data.success) {
        setMessage({ type: "error", text: (data.errors || ["Failed to remove member."]).join(", ") });
        clearMessage();
        return;
      }

      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      setMessage({ type: "success", text: `${name} removed from project.` });
      clearMessage();
    } catch (error) {
      setMessage({ type: "error", text: "Failed to remove member." });
      clearMessage();
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Box className="project-members-panel">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add or remove existing org users on this project.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          bgcolor: "grey.50",
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Add member
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "flex-end",
          }}
        >
          <Autocomplete
            sx={{ flex: "1 1 320px", minWidth: 320 }}
            options={userOptions}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            onInputChange={(_, value) => setSearchInput(value)}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={searchingUsers}
            noOptionsText={searchInput ? "No matching org users" : "Type to search org users"}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search org user"
                placeholder="Name or email"
                size="small"
              />
            )}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={adding ? null : <PersonAddIcon />}
            onClick={handleAdd}
            disabled={!selectedUser || adding}
            sx={{ minWidth: 96, height: 40 }}
          >
            {adding ? "Adding..." : "Add"}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: "grey.50",
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Current members
          </Typography>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              {members.length} {members.length === 1 ? "person" : "people"}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading members...
            </Typography>
          </Box>
        ) : members.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No members on this project yet.
            </Typography>
          </Box>
        ) : (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {members.map((member, index) => (
              <Box component="li" key={member.id}>
                {index > 0 && <Divider />}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {[member.first_name, member.last_name].filter(Boolean).join(" ") || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {member.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(member)}
                      disabled={removingId === member.id}
                      title="Remove from project"
                    >
                      {removingId === member.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectMembersPanel;
